<template>
  <v-card id="base-rate-contracts">
    <v-card-title :class="headerClass">
      <v-icon color="tertiary" class="mr-2">{{ baseRateMop.path }}</v-icon>
      <span class="headline">{{ $t('baseRateContracts') }}</span>
      <v-spacer/>
      <BaseDialogActions @refresh="refresh"/>
    </v-card-title>
    <v-card-text class="pt-4">
      <NoData v-if="!loading && summaries.length === 0">{{ $t('noBaseRateContracts') }}</NoData>
      <v-data-table
      v-else
      id="base-rate-contracts-table"
      dense
      hide-default-footer
      sort-by="ticketCount"
      sort-desc
      item-key="contractId"
      class="clickable-rows"
      :headers="headers"
      :items="summaries"
      :loading="loading"
      :items-per-page="-1"
      @click:row="summarySelected">
        <template #item.tract="{ item }">
          <Icon
          v-if="item.tractIsCertified"
          icon="mdi-certificate-outline"
          iconColor="black"
          :tooltipText="$t('tractIsCertified')"/>
          <span>{{ item.tract || $t('notApplicable') }}</span>
        </template>
        <template #item.fromAccount="{ item }">{{ item.fromAccount || $t('notApplicable') }}</template>
        <template #item.account="{ item }">
          <AccountName :certificationStatus="item.accountCertificationStatus">
            <span>{{ item.account }}</span>
          </AccountName>
        </template>
        <template #item.setting="{ item }">{{ item.setting || $t('notApplicable') }}</template>
        <template #item.ticketCount="{ item }">{{ numberWithCommas(item.ticketCount) }}</template>
        <template #item.actions="{ item }">
          <Icon
          icon="base-rate-mop"
          iconColor="primary"
          elementId="base-rate-contracts-details"
          :small="false"
          :tooltipText="$t('details')"
          @icon-clicked="summarySelected(item)"/>
        </template>
        <template #body.append v-if="summaries.length > 0">
          <tr class="font-weight-bold">
            <td :colspan="totalLabelColspan">{{ $t('total') }}</td>
            <td class="text-right">{{ numberWithCommas(totalTicketCount) }}</td>
            <td/>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
    <Dialog
    :stateId="drillInDialogId"
    :confirmClose="focusedApprovalId === undefined && finderHasUnsavedChanges"
    :enforcePersistence="focusedApprovalId !== undefined"
    @close="summaryClosed">
      <BaseRateFinder
      v-if="focusedSummary !== undefined"
      :summary="focusedSummary"
      :contractApprovalId="focusedApprovalId"
      :approvalLocked="focusedApprovalLocked"
      :headerClass="headerClass"
      @unsaved-changes="finderHasUnsavedChanges = $event"
      @saved="summarySaved"
      @finished="summaryFinished"/>
    </Dialog>
    <v-dialog v-if="approvalDialog" v-model="approvalDialog" persistent width="400px">
      <ConfirmDialog
      id="base-rate-approval-edit-dialog"
      :title="approvalDialogConfig.title"
      :body="approvalDialogConfig.body"
      color="primary"
      :confirmText="$t('edit')"
      @confirm="beginApprovalBasedEdit"
      @cancel="approvalDialog = false"/>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { numberWithCommas } from '@/utils/NumericMutations'
import { colorClassForContractMode, uniqueDialogId } from '@/utils/componentHelpers'
import { baseRateMop } from '@/images/AngelSvgLibrary'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { ContractApprovalStatus, ContractMode, UserClaims } from '@/utils/Enumerations.js'
import ContractHeaders from '@/headers/Contract'

export default {
  name: 'BaseRateContracts',

  components: {
    AccountName: () => import('@/components/account/AccountName.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    BaseRateFinder: () => import('@/components/contract/base-rate-finder/BaseRateFinder.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  props: {
    contractMode: {
      type: Object,
      required: true
    },
    includeProduction: {
      type: Boolean,
      required: true
    },
    includeWoodsSale: {
      type: Boolean,
      required: true
    },
    includeTransfer: {
      type: Boolean,
      required: true
    },
    includeLogYardSale: {
      type: Boolean,
      required: true
    }
  },

  data: () => ({
    baseRateMop,
    summaries: [],
    focusedContractId: undefined,
    focusedApprovalId: undefined,
    focusedApprovalLocked: false,
    finderHasUnsavedChanges: false,
    pendingSummary: undefined,
    approvalDialog: false,
    drillInDialogId: uniqueDialogId('base-rate-finder')
  }),

  computed: {
    ...mapGetters('base-rate-finder', ['loading']),
    ...mapGetters('user', ['companyInfo', 'userInfo']),

    approvalDialogConfig () {
      return this.pendingSummary !== undefined && this.previousModificationByUser(this.pendingSummary)
        ? { title: this.$t('resumeEditContractConfirmTitle'), body: this.$t('resumeEditContractConfirmMessage') }
        : { title: this.$t('contractEditConfirmTitle'), body: this.$t('contractEditConfirmMessage') }
    },

    canManageContracts () {
      return userAssignedClaim(UserClaims.ContractManager)
    },

    headerClass () {
      return colorClassForContractMode(this.contractMode.value)
    },

    isLogsMode () {
      return this.contractMode.value === ContractMode.Logs.value
    },

    headers () {
      return ContractHeaders.baseRateSummaryHeaders(this.isLogsMode)
    },

    totalLabelColspan () {
      return this.headers.findIndex(header => header.value === 'ticketCount')
    },

    totalTicketCount () {
      return this.summaries.reduce((count, summary) => count + summary.ticketCount, 0)
    },

    focusedSummary () {
      return this.summaries.find(summary => summary.contractId === this.focusedContractId)
    }
  },

  async created () {
    await this.refresh()
  },

  methods: {
    ...mapActions('base-rate-finder', ['fetchBaseRateSummaries']),
    ...mapActions('contract-approvals', ['createContractApproval', 'getMostRecentContractApproval']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    numberWithCommas,

    async refresh () {
      this.summaries = await this.fetchBaseRateSummaries({
        includeProduction: this.includeProduction,
        includeWoodsSale: this.includeWoodsSale,
        includeTransfer: this.includeTransfer,
        includeLogYardSale: this.includeLogYardSale
      })
      if (this.focusedContractId !== undefined && this.focusedSummary === undefined) this.closeSummary()
    },

    summarySelected (summary) {
      if (!this.canManageContracts || !this.companyInfo?.requireApprovalForContractModifications || summary.isDraft) {
        this.openSummary(summary, undefined, false)
      } else if (this.previousModificationByUser(summary) || this.approvalStateAllowsModification(summary)) {
        this.pendingSummary = summary
        this.approvalDialog = true
      } else {
        this.openSummary(summary, undefined, true)
      }
    },

    previousModificationByUser (summary) {
      return summary.approvalStatus === ContractApprovalStatus.PendingModification.value &&
        summary.approvalRequestedByUserId === this.userInfo.applicationUserId
    },

    approvalStateAllowsModification (summary) {
      return [ContractApprovalStatus.Approved.value, ContractApprovalStatus.Returned.value, null].includes(summary.approvalStatus)
    },

    async beginApprovalBasedEdit () {
      const summary = this.pendingSummary
      this.approvalDialog = false
      this.pendingSummary = undefined

      const approval = this.previousModificationByUser(summary)
        ? await this.getMostRecentContractApproval(summary.contractId)
        : await this.createContractApproval({ contractId: summary.contractId })
      if (!approval) return

      const modifiedSummary = {
        ...summary,
        approvalStatus: ContractApprovalStatus.PendingModification.value,
        approvalRequestedByUserId: this.userInfo.applicationUserId
      }
      this.summaries = this.summaries.map(s => s.contractId === summary.contractId ? modifiedSummary : s)
      this.openSummary(modifiedSummary, approval.contractApprovalId, false)
    },

    openSummary (summary, contractApprovalId, approvalLocked) {
      this.focusedApprovalId = contractApprovalId
      this.focusedApprovalLocked = approvalLocked
      this.focusedContractId = summary.contractId
      this.openOrUpdateDialog({ id: this.drillInDialogId, width: '80vw' })
    },

    summarySaved (contractId) {
      this.closeSummary()
      this.summaries = this.summaries.filter(summary => summary.contractId !== contractId)
    },

    async summaryFinished () {
      this.closeSummary()
      await this.refresh()
    },

    summaryClosed () {
      this.focusedContractId = undefined
      this.focusedApprovalId = undefined
      this.focusedApprovalLocked = false
      this.finderHasUnsavedChanges = false
    },

    closeSummary () {
      this.focusedApprovalId = undefined
      this.finderHasUnsavedChanges = false
      this.$nextTick(() => this.closeDialogsAtOrAbove(this.drillInDialogId))
    }
  }
}
</script>

<style scoped>
.clickable-rows ::v-deep tbody > tr:not(.font-weight-bold) {
  cursor: pointer;
}
</style>
